import {Box, Flex, LinkBox, LinkOverlay, Text} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {useClient} from '@/haligator-factory.tsx';
import {useHalCollection} from '@synako/haligator';
import {RightSingleArrow} from '@/components/svg/RightSingleArrowIcon.tsx';
import {routes} from 'ff-common';
import {
  OperationCommercialeListeDirection,
  OperationsCommercialesListe,
} from '@/domains/operation_commerciale/components/OperationsCommercialesListe.tsx';
import {useNavigate} from 'react-router-dom';
import {useClientRoute} from '@/hooks/auth/useClientRoute';

export type OperationsCommercialesListeViewContext =
  | 'search_bar'
  | 'accueil'
  | 'menu_catalogue'
  | 'liste'
  | 'search_sidebar';

interface OperationCommercialeListeViewProps {
  context?: OperationsCommercialesListeViewContext;
  onRedirect?: () => void;
}

/**
 * Affiche la liste des opérations commerciales éligibles pour le client.
 * Réutilisé dans plusieurs contextes (accueil, recherche, menu catalogue, onglet "En ce moment").
 * Le rendu s'adapte selon le contexte : nombre d'opérations, direction, titre, etc.
 */
export const OperationCommercialeListeView = ({
  context = 'accueil',
  onRedirect,
}: OperationCommercialeListeViewProps) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  const operation_commerciale_nav = useClient().follow(
    'operations_commerciales_eligible',
  );
  const operation_commerciale_data = useHalCollection(
    operation_commerciale_nav,
  );

  if (
    !operation_commerciale_data.items ||
    operation_commerciale_data.items.length === 0
  ) {
    return <></>;
  }

  let max_oc; // Maximum des opérations commerciales à afficher
  let margin_top; // Marge supérieure du conteneur
  let title_font_size = {base: 'sm', md: 'xl', lg: '2xl'};
  let button_font_size = {base: 'sm', md: 'md'};
  let display_title = true; // Affichage du titre de la section
  const title = t('accueil.operation_commerciale.titre');
  let direction: OperationCommercialeListeDirection = 'row'; // Direction d'affichage des opérations commerciales
  let afficher_header = true; // Affichage de l'en-tête de la section
  let afficher_voir_tout = true; // Affichage du bouton "Voir tout"
  let background: string | undefined = 'white'; // Fond de la section
  let title_color = 'blue.main'; // Couleur du titre de la section

  // Visualisation des opérations commerciales selon le contexte
  switch (context) {
    case 'search_bar':
      max_oc = 2;
      title_font_size = {base: 'sm', md: 'sm', lg: 'sm'};
      button_font_size = {base: 'xs', md: 'sm'};
      break;
    case 'search_sidebar':
      max_oc = 2;
      title_font_size = {base: 'sm', md: 'sm', lg: 'sm'};
      direction = 'column';
      afficher_voir_tout = false;
      break;
    case 'accueil':
      margin_top = {base: '50px', md: '40px'};
      direction = 'grille';
      afficher_voir_tout = false;
      // Section posée directement sur le fond de la page d'accueil
      background = undefined;
      title_color = 'blue.text';
      break;
    case 'menu_catalogue':
      display_title = false;
      direction = 'column';
      afficher_voir_tout = false;
      break;
    case 'liste':
      direction = 'deux_colonnes';
      afficher_header = false;
      break;
  }
  const operation_commerciale_collection = operation_commerciale_data.items.map(
    (oc) => oc.data,
  );
  const operations_commerciales = max_oc
    ? operation_commerciale_collection.slice(0, max_oc)
    : operation_commerciale_collection;

  const voir_tout_button_text = t('accueil.operation_commerciale.voir_tout');
  const href = buildClientRoute(
    routes.client.mes_produits_operation_commerciale,
  );

  const handleOnRedirect = () => {
    navigate(href);
    if (onRedirect) {
      onRedirect();
    }
  };

  return (
    <Box
      px={context === 'search_sidebar' ? '0' : {base: '2', md: '4', lg: '6'}}
      position='relative'
      mt={margin_top}
      bg={background}
    >
      {afficher_header && (
        <Flex
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mb={3}
        >
          {display_title && (
            <Text
              fontSize={title_font_size}
              fontWeight='700'
              color={title_color}
            >
              {title}
            </Text>
          )}
          {afficher_voir_tout && (
            <LinkBox>
              <LinkOverlay
                onClick={handleOnRedirect}
                color='gray.dark'
                fontWeight='600'
                _hover={{backgroundColor: 'transparent', cursor: 'pointer'}}
                p={{base: 0, md: 'inherit'}}
              >
                <Flex
                  direction='row'
                  alignItems='center'
                  gap={2}
                >
                  <Text
                    display={{base: 'block', md: 'none'}}
                    fontSize={button_font_size}
                  >
                    {voir_tout_button_text}
                  </Text>
                  <Text
                    display={{base: 'none', md: 'block'}}
                    fontSize={button_font_size}
                  >
                    {voir_tout_button_text}
                  </Text>
                  <RightSingleArrow />
                </Flex>
              </LinkOverlay>
            </LinkBox>
          )}
        </Flex>
      )}

      <Box
        overflowX={direction === 'row' ? 'auto' : undefined}
        width='100%'
      >
        <OperationsCommercialesListe
          operations_commerciales={operations_commerciales}
          context={context}
          direction={direction}
          onRedirect={onRedirect}
        />
      </Box>
    </Box>
  );
};
